import { useState, useEffect, useContext } from 'react';
import { useBoolean, usePopover } from 'minimal-shared/hooks';

import { useRouter } from 'src/routes/hooks';

import { useTranslate } from 'src/locales';
import { useLogout } from 'src/hook/useLogout';
import { useAppSelector } from 'src/hook/hooks';
import { type RootState } from 'src/redux/store';
import { useGetMediaUrlMutation } from 'src/pages/chat/slice/chatApi';
import { useVoiceCallAllowed } from 'src/hook/use-voice-call-allowed';
import { CallContext } from 'src/pages/chat/channel/call/hooks/call-context';
import { useModulePermissions } from 'src/pages/auth/hooks/useModulePermissions';

import { toast } from 'src/components/snackbar';

// ----------------------------------------------------------------------

export function useAccountDrawer(onDrawerItemHandler?: (id: string) => void) {
  const { t } = useTranslate('translation');
  const callContext = useContext(CallContext);
  const { reconnectSIP, registered, isConnecting } = callContext || {};

  const { loginApiData } = useAppSelector(
    (state: RootState & { auth: RootState['auth'] }) => state.auth
  );
  const voiceCallAllowed = useVoiceCallAllowed();
  const { isView: isChannelView } = useModulePermissions('channel');
  const isVoiceFeatureVisible = isChannelView && voiceCallAllowed;

  const { logoutHandler } = useLogout();

  const { open, onClose, onOpen, anchorEl } = usePopover();
  const [getMediaUrl] = useGetMediaUrlMutation();

  const [selectedContactId, setSelectedContactId] = useState<string[] | null>(null);
  const [profileImgUrl, setProfileImgUrl] = useState('');
  const changePassWordMenu = useBoolean();
  const router = useRouter();

  const handleSelectContact = (id: string) => {
    if (id === 'CHANGE_PASSWORD') {
      changePassWordMenu.onTrue();
      onClose();
      return;
    } else if (['TOGGLE_THEME', 'DIALER', 'INTERNAL_CALL'].includes(id)) {
      if (onDrawerItemHandler) {
        onDrawerItemHandler(id);
      }
      onClose();
      return;
    }
    router.replace(id);
    onClose();
  };

  const signOutHandler = async () => {
    try {
      onClose();
      await logoutHandler();
    } catch (error) {
      console.error('Logout failed:', error);
      toast.error(t('SOMETHING_WENT_WRONG'));
    }
  };

  useEffect(() => {
    const getImgUrl = async () => {
      const filePath = loginApiData && 'ProfileURL' in loginApiData && loginApiData.ProfileURL;
      if (!filePath) {
        setProfileImgUrl('');
        return;
      }

      const { data: uploadFileUrl } = await getMediaUrl({ fileName: filePath || '' });

      if (uploadFileUrl) {
        setProfileImgUrl(uploadFileUrl);
      }
    };

    if (loginApiData && 'ProfileURL' in loginApiData && loginApiData?.ProfileURL) {
      getImgUrl();
    }
  }, [loginApiData, getMediaUrl]);

  const { activeSubscription } = useAppSelector((state: RootState) => state.setting);
  const { isView } = useModulePermissions('user');

  const currentUserId =
    loginApiData && typeof loginApiData === 'object' && 'id' in loginApiData && loginApiData.id
      ? String((loginApiData as { id: string | number }).id)
      : undefined;

  return {
    t,
    open,
    onOpen,
    onClose,
    loginApiData,
    registered,
    isConnecting,
    reconnectSIP,
    profileImgUrl,
    selectedContactId,
    changePassWordMenu,
    handleSelectContact,
    signOutHandler,
    anchorEl,
    activeSubscription,
    isView,
    isVoiceFeatureVisible,
    currentUserId,
  };
}
